// src/components/merchant/MyDeals.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiEdit, FiTrash2, FiEye, FiPlus, FiSearch, FiRefreshCw, FiToggleLeft, FiToggleRight, FiEyeOff, FiClock, FiAlertTriangle, FiCheckCircle, FiCopy } from 'react-icons/fi';
import { toast } from 'react-toastify';
import API, { merchantAPI } from '../../services/api';
import { getDealImageUrl } from '../../utils/dealImageHelper';
import PlanQuotaBanner from '../../components/merchant/PlanQuotaBanner';

const fmtCurrency = (value) =>
  typeof value === 'number'
    ? value.toLocaleString('fr-FR') + ' MAD'
    : value
    ? `${value} MAD`
    : '0 MAD';

const MyDeals = () => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [duplicatingId, setDuplicatingId] = useState(null);
  const navigate = useNavigate();
  const [renewingId, setRenewingId] = useState(null);
  const [confirm, setConfirm] = useState(null); // { type:'renew'|'delete', id, title, message, confirmLabel }
  const [error, setError] = useState(null);

  const mountedRef = useRef(true);
  const abortRef = useRef(null);
  const debounceTimer = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      try {
        abortRef.current?.abort();
      } catch (e) {}
      clearTimeout(debounceTimer.current);
    };
  }, []);

  const extractListFromResponse = (res) => {
    const obj = res?.data ?? res ?? null;
    if (!obj) return [];
    const payload = obj?.data ?? obj;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.deals)) return payload.deals;
    if (Array.isArray(payload?.items)) return payload.items;
    return [];
  };

  const tryGet = async (url, signal) => {
    try {
      const res = await API.get(url, { signal });
      return { ok: true, data: extractListFromResponse(res) };
    } catch (err) {
      const isAbort =
        err?.name === 'AbortError' ||
        err?.code === 'ERR_CANCELED' ||
        err?.message === 'canceled';
      if (isAbort) throw err;
      return { ok: false, err };
    }
  };

  const fetchDeals = useCallback(async () => {
    setError(null);
    setLoading(true);

    try {
      try {
        abortRef.current?.abort();
      } catch (e) {}
      abortRef.current = new AbortController();
      const signal = abortRef.current.signal;

      const endpoints = ['/merchant/me/deals', '/merchant/deals', '/deals'];

      let final = [];
      let lastError = null;

      for (const ep of endpoints) {
        try {
          const r = await tryGet(ep, signal);
          if (r.ok) {
            final = Array.isArray(r.data) ? r.data : [];
            break;
          } else {
            lastError = r.err;
            continue;
          }
        } catch (abortErr) {
          throw abortErr;
        }
      }

      if (mountedRef.current) {
        setDeals(final);
        if (final.length === 0 && lastError) {
          const status = lastError?.response?.status;
          if (status && status !== 404)
            setError('Impossible de récupérer les deals (erreur serveur)');
        }
      }
    } catch (err) {
      if (
        err?.name === 'AbortError' ||
        err?.code === 'ERR_CANCELED' ||
        err?.message === 'canceled'
      )
        return;
      console.error('fetchDeals error', err);
      if (mountedRef.current) {
        setError('Impossible de récupérer les deals');
        toast.error('Erreur lors du chargement des deals');
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchDeals();
  }, [fetchDeals]);

  const handleRenew = async (id) => {
    if (!id) return;
    setRenewingId(id);
    try {
      await merchantAPI.renewDeal(id, 30);
      toast.success('Deal renouvelé pour 30 jours');
      if (mountedRef.current) fetchDeals();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Erreur lors du renouvellement');
    } finally {
      if (mountedRef.current) setRenewingId(null);
    }
  };

  const runConfirm = () => {
    const c = confirm;
    setConfirm(null);
    if (!c) return;
    if (c.type === 'renew') handleRenew(c.id);
    else if (c.type === 'delete') handleDelete(c.id);
  };

  const handleDelete = async (id) => {
    setDeletingId(id);

    const prev = deals;
    setDeals((prevList) => prevList.filter((d) => (d._id || d.id) !== id));

    try {
      await API.delete(`/deals/id/${id}`);
      toast.success('Deal supprimé');
    } catch (err) {
      console.error('delete deal error', err);
      toast.error(
        err?.response?.data?.message || 'Erreur lors de la suppression'
      );
      setDeals(prev);
      setTimeout(() => {
        if (mountedRef.current) fetchDeals();
      }, 500);
    } finally {
      if (mountedRef.current) setDeletingId(null);
    }
  };

  // Duplication : 5 annonces quasi identiques (le prix ou une photo changent) ne
  // se recréent pas à zéro. On copie, puis on ouvre directement la copie en édition.
  const duplicateDeal = async (deal) => {
    const id = deal._id || deal.id;
    if (!id || duplicatingId) return;
    setDuplicatingId(id);
    try {
      const res = await merchantAPI.duplicateDeal(id);
      const data = res?.data;
      toast.success(data?.message || 'Annonce dupliquée');
      const copyId = data?.data?._id || data?.data?.id;
      if (copyId) {
        navigate(`/merchant/deals/${copyId}/edit`);
      } else {
        fetchDeals();
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur lors de la duplication');
    } finally {
      if (mountedRef.current) setDuplicatingId(null);
    }
  };

  const [filteredDeals, setFilteredDeals] = useState([]);
  const toggleDealEnabled = async (deal) => {
    const id = deal._id || deal.id;
    if (!id) return;
    const cur = deal.enabled !== false;
    const apply = (val) => (d) => ((d._id || d.id) === id ? { ...d, enabled: val } : d);
    setDeals((prev) => prev.map(apply(!cur)));
    setFilteredDeals((prev) => prev.map(apply(!cur)));
    try {
      const res = await API.patch(`/deals/${id}/toggle`);
      const next = res && res.data && typeof res.data.enabled === 'boolean' ? res.data.enabled : !cur;
      setDeals((prev) => prev.map(apply(next)));
      setFilteredDeals((prev) => prev.map(apply(next)));
      toast.success((res && res.data && res.data.message) || (next ? 'Deal activé' : 'Deal désactivé'));
    } catch (e) {
      setDeals((prev) => prev.map(apply(cur)));
      setFilteredDeals((prev) => prev.map(apply(cur)));
      toast.error('Action impossible');
    }
  };

  useEffect(() => {
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      const q = (searchTerm || '').trim().toLowerCase();
      if (!q) {
        setFilteredDeals(deals);
        return;
      }
      setFilteredDeals(
        deals.filter((deal) => {
          const title = (deal.title || deal.name || '')
            .toString()
            .toLowerCase();
          // `deal.category` est le SLUG cote API ; le nom lisible est dans
          // `categoryName`. On cherche dans les deux : un marchand tape
          // « décoration », pas « maison-d-coration ».
          const category = (deal.categoryName || deal.category?.name || deal.category || '')
            .toString()
            .toLowerCase();
          return title.includes(q) || category.includes(q);
        })
      );
    }, 220);
    return () => clearTimeout(debounceTimer.current);
  }, [searchTerm, deals]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  const toNumber = (...vals) => {
    for (const v of vals) {
      if (v === null || v === undefined) continue;
      if (typeof v === 'number' && Number.isFinite(v)) return v;

      const cleaned = String(v)
        .replace(/[^\d,.,-]/g, '')
        .replace(',', '.');

      const n = parseFloat(cleaned);
      if (Number.isFinite(n)) return n;
    }
    return 0;
  };

  // ================================================================
  // Vue mobile / vue bureau
  //
  // ATTENTION : ces fonctions doivent rester ICI, APRES le retour anticipe
  // `if (loading)`. Elles s'etaient retrouvees a l'interieur de ce bloc :
  // une fois le chargement termine, le bloc n'etait plus execute, `buildRow`
  // n'existait donc pas et le rendu levait "buildRow is not defined" —
  // c'est-a-dire un ecran blanc sur toute la page. Elles utilisent aussi
  // `toNumber`, defini juste au-dessus : ne pas les remonter plus haut.
  //
  // Le tableau etait enferme dans un conteneur `overflow-hidden` : sur
  // telephone, les colonnes Prix, Statut, Stock et surtout ACTIONS sortaient
  // de l'ecran et etaient purement COUPEES, sans defilement possible. Un
  // marchand ne pouvait donc ni modifier, ni desactiver, ni supprimer une
  // annonce depuis son telephone.
  //
  // Corrige en deux temps : cartes empilees sur mobile (toutes les actions
  // accessibles au pouce), tableau defilant horizontalement a partir de `md`.
  // Les donnees et les boutons sont calcules UNE fois ici, pour que les deux
  // vues ne puissent pas divergerune fois l'une des deux modifiee.
  // ================================================================
  const buildRow = (deal) => {
    const id = deal._id || deal.id;
    const endDate = deal.endDate || deal.validUntil || null;
    const startDate = deal.startDate || deal.validFrom || null;
    return {
      id,
      deal,
      categoryName: deal.categoryName || deal.category?.name || deal.category || '—',
      price: toNumber(deal.discountedPrice, deal.price, deal.currentPrice, deal.promoPrice),
      originalPrice: toNumber(deal.originalPrice, deal.basePrice, deal.oldPrice, deal.price),
      imgUrl: getDealImageUrl(deal, 0, ''),
      sold: deal.soldCount ?? deal.quantitySold ?? 0,
      total: deal.quantityTotal ?? deal.quantity ?? deal.quantityRemaining ?? '—',
      isExpired:
        deal.status === 'expired' ||
        (!!endDate && new Date(endDate).getTime() < Date.now()),
      isScheduled: !!startDate && new Date(startDate).getTime() > Date.now()
    };
  };

  const StatusBadge = ({ deal, isExpired, isScheduled }) => {
    if (deal.enabled === false) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
          <FiEyeOff size={11} /> Désactivé
        </span>
      );
    }
    if (isExpired) {
      return (
        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
          Expiré
        </span>
      );
    }
    if (deal.status === 'active') {
      return isScheduled ? (
        <span
          className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800"
          title="Validée. Mise en ligne automatique à la date de début."
        >
          <FiClock size={11} /> Programmé
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
          <FiCheckCircle size={11} /> En ligne
        </span>
      );
    }
    if (deal.status === 'pending') {
      return (
        <span
          className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800"
          title="Une annonce n'est visible du public qu'après validation par un administrateur."
        >
          <FiClock size={11} /> En attente de validation
        </span>
      );
    }
    if (deal.status === 'rejected') {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
          <FiAlertTriangle size={11} /> Refusée
        </span>
      );
    }
    return (
      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">
        {deal.status || '—'}
      </span>
    );
  };

  // `big` : sur mobile les cibles tactiles sont agrandies (44 px recommandes),
  // sinon les icones de 18 px sont difficiles a atteindre au pouce.
  const RowActions = ({ deal, id, isExpired, big = false }) => {
    const btn = big
      ? 'inline-flex items-center justify-center w-11 h-11 rounded-lg border border-gray-200'
      : '';
    const sz = big ? 20 : 18;
    return (
      <div className={`flex items-center ${big ? 'flex-wrap gap-2' : 'justify-end space-x-2'}`}>
        <Link
          to={`/deals/${deal.slug || id}`}
          className={`text-blue-600 hover:text-blue-900 ${btn}`}
          aria-label="Voir"
          title="Voir"
        >
          <FiEye size={sz} />
        </Link>
        <button
          type="button"
          onClick={() => duplicateDeal(deal)}
          disabled={duplicatingId === id}
          className={`text-indigo-600 hover:text-indigo-900 disabled:opacity-50 ${btn}`}
          title="Dupliquer cette annonce"
          aria-label="Dupliquer"
        >
          <FiCopy size={sz} className={duplicatingId === id ? 'animate-pulse' : ''} />
        </button>
        <button
          type="button"
          onClick={() => toggleDealEnabled(deal)}
          className={`${deal.enabled === false ? 'text-red-600 hover:text-red-800' : 'text-green-600 hover:text-green-800'} ${btn}`}
          title={deal.enabled === false ? 'Activer le deal' : 'Désactiver le deal'}
          aria-label={deal.enabled === false ? 'Activer' : 'Désactiver'}
        >
          {deal.enabled === false ? <FiToggleLeft size={sz + 2} /> : <FiToggleRight size={sz + 2} />}
        </button>
        <Link
          to={`/merchant/deals/${id}/edit`}
          state={{ deal }}
          className={`text-green-600 hover:text-green-900 ${btn}`}
          aria-label="Éditer"
          title="Modifier"
        >
          <FiEdit size={sz} />
        </Link>
        {isExpired && (
          <button
            type="button"
            onClick={() =>
              setConfirm({
                type: 'renew',
                id,
                title: 'Renouveler le deal',
                message: 'Ce deal sera prolongé de 30 jours et réactivé.',
                confirmLabel: 'Renouveler'
              })
            }
            className={`text-amber-600 hover:text-amber-800 disabled:opacity-50 ${btn}`}
            disabled={renewingId === id}
            title="Renouveler (30 jours)"
            aria-label="Renouveler"
          >
            <FiRefreshCw size={sz} className={renewingId === id ? 'animate-spin' : ''} />
          </button>
        )}
        <button
          type="button"
          onClick={() =>
            setConfirm({
              type: 'delete',
              id,
              title: 'Supprimer le deal',
              message: 'Cette action est irréversible.',
              confirmLabel: 'Supprimer'
            })
          }
          className={`text-red-600 hover:text-red-900 ${btn}`}
          disabled={deletingId === id}
          title="Supprimer"
          aria-disabled={deletingId === id}
        >
          <FiTrash2 size={sz} />
        </button>
      </div>
    );
  };

  const ModerationNotice = ({ deal }) => {
    if (deal.status === 'pending') {
      return (
        <div className="mt-1 text-xs text-yellow-800 bg-yellow-50 border border-yellow-200 rounded px-2 py-1">
          Visible uniquement par vous tant qu'un administrateur ne l'a pas validée.
        </div>
      );
    }
    if (deal.status === 'rejected' && deal.rejectionReason) {
      return (
        <div className="mt-1 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
          <strong>Motif du refus :</strong> {deal.rejectionReason}
          <span className="block mt-0.5 text-red-600">
            Corrigez l'annonce puis enregistrez : elle repartira automatiquement en validation.
          </span>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="p-6 pb-24">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Mes Deals</h1>
        <Link
          to="/merchant/deals/new"
          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <FiPlus className="mr-2" /> Nouveau Deal
        </Link>
      </div>

      {/* Consommation du pack : c'est ici que le marchand compte ses annonces,
          donc ici que la limite doit se lire — pas seulement sur la page
          Abonnement, qu'il n'ouvre presque jamais. */}
      <PlanQuotaBanner />

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher un deal..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        {error && <div className="mt-2 text-sm text-red-600">{error}</div>}
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {filteredDeals.length > 0 ? (
          <>
            {/* ===== MOBILE : cartes empilees ===== */}
            <div className="md:hidden divide-y divide-gray-200">
              {filteredDeals.map((deal) => {
                const r = buildRow(deal);
                return (
                  <div key={r.id} className="p-4">
                    <div className="flex gap-3">
                      {r.imgUrl ? (
                        <img
                          src={r.imgUrl}
                          alt={deal.title || 'image'}
                          className="w-20 h-20 object-cover rounded shrink-0"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = '/placeholder.jpg';
                          }}
                        />
                      ) : (
                        <div className="w-20 h-20 bg-gray-100 flex items-center justify-center rounded text-xs text-gray-400 shrink-0">
                          No img
                        </div>
                      )}

                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-gray-900 break-words">
                          {deal.title || deal.name || 'Sans titre'}
                        </div>
                        <div className="text-sm text-gray-500">{r.categoryName}</div>
                        <div className="mt-1 flex items-baseline gap-2">
                          <span className="text-sm font-semibold">{fmtCurrency(r.price)}</span>
                          {r.originalPrice > r.price && (
                            <span className="text-xs text-gray-500 line-through">
                              {fmtCurrency(r.originalPrice)}
                            </span>
                          )}
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <StatusBadge deal={deal} isExpired={r.isExpired} isScheduled={r.isScheduled} />
                          <span className="text-xs text-gray-500">
                            Vendus : {r.sold} / {r.total}
                          </span>
                        </div>
                      </div>
                    </div>

                    <ModerationNotice deal={deal} />

                    <div className="mt-3 pt-3 border-t">
                      <RowActions deal={deal} id={r.id} isExpired={r.isExpired} big />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ===== BUREAU : tableau, defilement horizontal si necessaire ===== */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Image</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deal</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prix</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendus</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredDeals.map((deal) => {
                    const r = buildRow(deal);
                    return (
                      <tr key={r.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 w-24">
                          {r.imgUrl ? (
                            <img
                              src={r.imgUrl}
                              alt={deal.title || 'image'}
                              className="w-20 h-12 object-cover rounded"
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = '/placeholder.jpg';
                              }}
                            />
                          ) : (
                            <div className="w-20 h-12 bg-gray-100 flex items-center justify-center rounded text-xs text-gray-400">
                              No img
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 max-w-xs">
                          <div className="font-medium text-gray-900 truncate">
                            {deal.title || deal.name || 'Sans titre'}
                          </div>
                          <div className="text-sm text-gray-500">{r.categoryName}</div>
                          <ModerationNotice deal={deal} />
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-semibold">{fmtCurrency(r.price)}</div>
                          {r.originalPrice > r.price && (
                            <div className="text-xs text-gray-500 line-through">
                              {fmtCurrency(r.originalPrice)}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <StatusBadge deal={deal} isExpired={r.isExpired} isScheduled={r.isScheduled} />
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {r.sold} / {r.total}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <RowActions deal={deal} id={r.id} isExpired={r.isExpired} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <FiPlus className="mx-auto text-4xl text-gray-400 mb-2" />
            <p className="text-gray-600">Aucun deal créé</p>
            <Link
              to="/merchant/deals/new"
              className="text-primary-600 hover:underline mt-2 inline-block"
            >
              Créer votre premier deal
            </Link>
          </div>
        )}
      </div>

      {confirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setConfirm(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5">
              <div className="flex items-start gap-3">
                <span
                  className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                    confirm.type === 'delete'
                      ? 'bg-red-50 text-red-600'
                      : 'bg-amber-50 text-amber-600'
                  }`}
                >
                  {confirm.type === 'delete' ? <FiTrash2 size={20} /> : <FiRefreshCw size={20} />}
                </span>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">{confirm.title}</h3>
                  <p className="mt-1 text-sm text-gray-500">{confirm.message}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-3 bg-gray-50">
              <button
                type="button"
                onClick={() => setConfirm(null)}
                className="px-4 py-2 text-sm font-medium text-gray-600 rounded-lg hover:bg-gray-100"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={runConfirm}
                className={`px-4 py-2 text-sm font-semibold text-white rounded-lg ${
                  confirm.type === 'delete'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-primary-600 hover:bg-primary-700'
                }`}
              >
                {confirm.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyDeals;